import { useCallback, useEffect, useRef, useState } from 'react';
import { Timeline, type TimelineItem, type TimelineTone } from '../../../components/shared/Timeline/Timeline';
import {
  getMenuAccessHistory, type MenuHistoryItem, type MenuHistoryScope, type MenuHistoryValue,
} from '../../../api/menu-access';

/**
 * 메뉴 권한 — 변경 이력 (2026-09-15 · 메뉴 권한 개편 R5 · 서버 PR #1386 · 확정 시안 v4 `renderHist`).
 *
 * ★ 한 대상 기준이다 — 부서별 탭은 고른 조직, 개인별 탭은 고른 사람(서버가 개인 이력에 부서 · 회사 변경을 안 섞는다).
 * ★ 줄은 정본 E9 Timeline(최신이 위 · `<ol>` + `<time>`). 문구는 저장 알림과 같은 어법(「인사팀 · 「메일」 상속 → 차단」).
 * ★★ 감사 기록이 비동기다 — 저장 직후 곧바로 부르면 방금 바꾼 것이 늦게 보인다.
 *    그래서 저장 신호(`refreshTick`)를 받으면 **700ms 뒤** 다시 부르고, 맨 위가 그대로면 **1.5초 뒤 한 번 더** 부른다.
 *    낙관적 줄은 넣지 않는다 — 이전 값을 화면이 추측하면 틀린 이력이 선다.
 */

export const HISTORY_PAGE = 20;
export const HISTORY_REFETCH_MS = 700;
export const HISTORY_RETRY_MS = 1500;

const OLD_RECORD = '이전 값이 남지 않은 옛 기록입니다';

export function valueWord(v: MenuHistoryValue | null, forUser: boolean): string {
  if (v === null) return '이전 값 없음';
  if (v === 'inherit') return forUser ? '부서 기준' : '상속';
  return { allow: '허용', deny: '차단', on: '켬', off: '끔' }[v];
}

function targetOf(item: MenuHistoryItem): string {
  if (item.scopeName) return item.scopeName;
  if (item.scopeType === 'company') return '회사';
  if (item.scopeType === 'unassigned') return '팀없는직원';
  return '(지워진 대상)';
}

function toneOf(v: MenuHistoryValue | null): TimelineTone {
  if (v === 'allow' || v === 'on') return 'success';
  if (v === 'deny' || v === 'off') return 'danger';
  return 'muted';
}

/** 기록 한 건 → 제목 · 덧붙임 · 점 색. */
export function historyEntry(item: MenuHistoryItem): { title: string; meta: string | null; tone: TimelineTone } {
  const actor = item.actor?.name ?? '(지워진 계정)';
  const forUser = item.scopeType === 'user';
  const target = targetOf(item);
  const one = (c: MenuHistoryItem['changes'][number]) =>
    `「${c.menuLabel}」 ${valueWord(c.before, forUser)} → ${valueWord(c.after, forUser)}`;

  if (item.kind === 'migrate') {
    const s = item.summary ?? {};
    return {
      title: `${actor} · 팀없는직원 묶음 규칙을 ${s.users ?? 0}명의 개인 설정으로 옮김`,
      meta: `${s.copied ?? 0}칸 옮김 · ${s.skipped ?? 0}칸 건너뜀 · 묶음 규칙 ${s.cleared ?? 0}개 지움`,
      tone: 'info',
    };
  }
  if (item.changes.length === 0) {
    if (item.kind === 'clear') {
      return { title: `${actor} · ${target} · 규칙 ${item.summary?.cleared ?? 0}개 지움`, meta: OLD_RECORD, tone: 'muted' };
    }
    return { title: `${actor} · ${target} · 바뀐 메뉴 없음`, meta: null, tone: 'muted' };
  }

  const list = item.changes.slice(0, 3).map(one).join(' · ')
    + (item.changes.length > 3 ? ` 외 ${item.changes.length - 3}` : '');
  const old = item.beforeKnown ? null : OLD_RECORD;
  const joined = [list, old].filter(Boolean).join(' — ');

  if (item.kind === 'clear') {
    return { title: `${actor} · ${target} · 규칙 ${item.changes.length}개 지움`, meta: joined, tone: 'muted' };
  }
  if (item.changes.length === 1) {
    const c = item.changes[0]!;
    return { title: `${actor} · ${target} · ${one(c)}`, meta: old, tone: toneOf(c.after) };
  }
  return { title: `${actor} · ${target} · 메뉴 ${item.changes.length}개 바꿈`, meta: joined, tone: 'info' };
}

const pad = (n: number) => String(n).padStart(2, '0');
function atLabel(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

type Status = 'loading' | 'ready' | 'failed' | 'forbidden';

export function HistoryPanel({ scope, title, refreshTick }: {
  /** null = 아직 사람을 안 골랐다(개인별 탭). */
  scope: MenuHistoryScope | null;
  title: string;
  /** 저장이 끝날 때마다 1씩 오른다. 열 때 이미 쌓인 값으로는 다시 부르지 않는다. */
  refreshTick: number;
}) {
  const [items, setItems] = useState<MenuHistoryItem[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [status, setStatus] = useState<Status>('loading');
  const [loadingMore, setLoadingMore] = useState(false);
  const [moreFailed, setMoreFailed] = useState(false);

  const scopeKey = scope ? `${scope.scopeType}:${scope.scopeId ?? ''}` : '';
  const seq = useRef(0);
  const newest = useRef<string | null>(null);
  const tickSeen = useRef(refreshTick);

  /** 첫 판을 다시 부른다 — 끝나면 맨 위 기록 id(실패 · 늦게 온 응답은 undefined). */
  const load = useCallback(async (): Promise<string | null | undefined> => {
    if (!scope) return undefined;
    const my = ++seq.current;
    try {
      const page = await getMenuAccessHistory({ ...scope, limit: HISTORY_PAGE });
      if (my !== seq.current) return undefined;
      setItems(page.items);
      setCursor(page.nextCursor);
      setMoreFailed(false);
      setStatus('ready');
      newest.current = page.items[0]?.id ?? null;
      return newest.current;
    } catch (e) {
      if (my !== seq.current) return undefined;
      const code = (e as { response?: { status?: number } })?.response?.status;
      setStatus(code === 403 ? 'forbidden' : 'failed');
      return undefined;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopeKey]);

  useEffect(() => {
    setItems([]);
    setCursor(null);
    newest.current = null;
    if (!scope) return;
    setStatus('loading');
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopeKey]);

  useEffect(() => {
    if (refreshTick === tickSeen.current) return;
    tickSeen.current = refreshTick;
    if (!scope) return;
    const before = newest.current;
    let cancelled = false;
    let retry: ReturnType<typeof setTimeout> | undefined;
    const first = setTimeout(() => {
      void load().then((top) => {
        if (cancelled || top !== before) return;
        retry = setTimeout(() => { if (!cancelled) void load(); }, HISTORY_RETRY_MS);
      });
    }, HISTORY_REFETCH_MS);
    return () => {
      cancelled = true;
      clearTimeout(first);
      if (retry) clearTimeout(retry);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshTick]);

  const loadMore = async () => {
    if (!scope || !cursor || loadingMore) return;
    const my = seq.current;
    setLoadingMore(true);
    setMoreFailed(false);
    try {
      const page = await getMenuAccessHistory({ ...scope, limit: HISTORY_PAGE, cursor });
      if (my !== seq.current) return;
      setItems((xs) => [...xs, ...page.items.filter((it) => !xs.some((x) => x.id === it.id))]);
      setCursor(page.nextCursor);
    } catch {
      if (my === seq.current) setMoreFailed(true);
    } finally {
      setLoadingMore(false);
    }
  };

  const rows: TimelineItem[] = items.map((it) => {
    const e = historyEntry(it);
    return { id: it.id, title: e.title, meta: e.meta ?? undefined, tone: e.tone, at: it.at, atLabel: atLabel(it.at) };
  });

  let body;
  if (!scope) {
    body = <p className="mpx-dim">사람을 고르면 그 사람의 변경 이력이 보입니다.</p>;
  } else if (status === 'loading') {
    body = <p className="mpx-dim" aria-busy="true">불러오는 중…</p>;
  } else if (status === 'forbidden') {
    body = <p className="mpx-dim">변경 이력을 볼 수 있는 권한이 없습니다.</p>;
  } else if (status === 'failed') {
    body = (
      <p className="mpx-note mpx-note--bad" role="alert">
        변경 이력을 불러오지 못했습니다{' '}
        <button type="button" className="mpx-link" onClick={() => { setStatus('loading'); void load(); }}>다시 시도</button>
      </p>
    );
  } else if (rows.length === 0) {
    body = <p className="mpx-dim">아직 바뀐 기록이 없습니다.</p>;
  } else {
    body = (
      <>
        <Timeline items={rows} className="mpx-hist__list" />
        {moreFailed && <p className="mpx-note mpx-note--bad" role="alert">더 불러오지 못했습니다.</p>}
        {cursor && (
          <button type="button" className="mpx-btn mpx-hist__more" disabled={loadingMore} onClick={() => { void loadMore(); }}>
            {loadingMore ? '불러오는 중…' : '더 보기'}
          </button>
        )}
      </>
    );
  }

  return (
    <section className="mpx-hist" aria-label="변경 이력">
      <h4 className="mpx-hist__title">변경 이력 — {title}</h4>
      {body}
    </section>
  );
}

export default HistoryPanel;
